/*
  © 2024–2026 Atlas PHM
  Independently developed software. All rights reserved.
*/

import type { FuturePlan } from '@core/types';
import { PRIORITY_OPTIONS } from '../../../constants/priorityOptions';

interface Props {
  priorities: Array<{ key: string; rank: number }>;
  futurePlans?: FuturePlan[];
  onClick: () => void;
}

const FUTURE_PLAN_LABELS: Record<string, string> = {
  loft_conversion: 'Loft conversion / extension',
  rear_extension: 'Rear extension',
  side_extension: 'Side extension',
  garage_conversion: 'Garage conversion',
  additional_bathroom: 'Additional bathroom',
  solar_pv: 'Solar / heat-pump intent',
};

export function PrioritiesTile({ priorities, futurePlans = [], onClick }: Props) {
  const hasPriorities = priorities && priorities.length > 0;
  const selectedFuturePlans = futurePlans.filter((p) => p.key !== 'no_plans' && p.key !== 'unsure');
  const hasFuturePlans = selectedFuturePlans.length > 0;

  const getRankSuffix = (rank: number) => {
    const rem10 = rank % 10;
    const rem100 = rank % 100;
    if (rem10 === 1 && rem100 !== 11) return `${rank}st`;
    if (rem10 === 2 && rem100 !== 12) return `${rank}nd`;
    if (rem10 === 3 && rem100 !== 13) return `${rank}rd`;
    return `${rank}th`;
  };

  const getPriorityLabel = (key: string) => {
    const option = PRIORITY_OPTIONS.find(opt => opt.key === key);
    return option?.label || key.replace(/_/g, ' ');
  };

  const getFuturePlanLabel = (key: string) => FUTURE_PLAN_LABELS[key] || key.replace(/_/g, ' ');

  return (
    <div className="hub-tile" onClick={onClick} role="button" tabIndex={0}>
      <div className="hub-tile__header">
        <h3 className="hub-tile__title">Upgrade brief</h3>
        <span className={`hub-tile__badge hub-tile__badge--${hasPriorities || hasFuturePlans ? 'high' : 'low'}`}>
          {hasPriorities || hasFuturePlans ? `${priorities.length} priorities · ${selectedFuturePlans.length} plans` : 'not set'}
        </span>
      </div>
      <div className="hub-tile__body">
        {hasPriorities ? (
          [...priorities]
            .sort((a, b) => a.rank - b.rank)
            .map((priority) => (
              <div key={priority.key} className="hub-tile__row">
                <span className="hub-tile__label">{getRankSuffix(priority.rank)} priority</span>
                <span className="hub-tile__value">
                  {getPriorityLabel(priority.key)}
                </span>
              </div>
            ))
        ) : (
          <div className="hub-tile__row">
            <span className="hub-tile__value hub-tile__value--unknown">
              No priorities selected yet
            </span>
          </div>
        )}

        <div className="hub-tile__row">
          <span className="hub-tile__label">Future plans</span>
          <span className={`hub-tile__value${hasFuturePlans ? '' : ' hub-tile__value--unknown'}`}>
            {hasFuturePlans
              ? selectedFuturePlans.slice(0, 2).map((plan) => getFuturePlanLabel(plan.key)).join(', ')
              : 'Not set'}
          </span>
        </div>
      </div>

      {!hasPriorities && !hasFuturePlans && (
        <div className="hub-tile__warnings">
          <span className="hub-tile__warning">
            Define your upgrade brief and future plans to shape the recommendation
          </span>
        </div>
      )}

      <span className="hub-tile__tap-hint">tap to {hasPriorities || hasFuturePlans ? 'edit' : 'set'}</span>
    </div>
  );
}
